import { inject, Injectable } from '@angular/core';
import {
  EVENT_CLICK_TYPE,
  USER_LOGGED_IN_STATUS
} from '../../../../constants/utils';
import { DashboardResponse } from '../../../../models/login';
import {
  AnalyticsSiteAssets,
  AnalyticsUser,
  ClickEventAnalytics
} from '../../../../models/utils';
import { UserSessionService } from '../../../login/data-services/user-session/user-session.service';
import { AnalyticsService } from '../analytics.service';
import { AnalyticsDataPopulateHelperService } from './analytics-data-populate-helper.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsDataPopulateSupportService extends AnalyticsDataPopulateHelperService {
  protected analyticsService = inject(AnalyticsService);
  protected userSession = inject(UserSessionService);
  protected userStatusData: DashboardResponse | null = null;
  private getLoginState(isWarmState: boolean): string {
    return isWarmState ? USER_LOGGED_IN_STATUS.HOT : USER_LOGGED_IN_STATUS.COLD;
  }

  getLoginMethod(): string {
    const sessionLoginMethod = sessionStorage.getItem('qm_login_method');
    if (sessionLoginMethod) {
      return sessionLoginMethod;
    }
    return '';
  }

  private createUserAttributes(): AnalyticsUser['attributes'] {
    const isLoggedIn = this.userStatusData?.loggedIn;
    const baseAttributes: AnalyticsUser['attributes'] = {
      is_logged_in: isLoggedIn
        ? USER_LOGGED_IN_STATUS.AUTHENTICATED
        : USER_LOGGED_IN_STATUS.UNAUTHENTICATED
    };
    if (isLoggedIn) {
      baseAttributes.login_method = this.getLoginMethod();
      baseAttributes.login_state = this.getLoginState(
        this.userStatusData?.isWarmState ?? false
      );
    }
    return baseAttributes;
  }

  private createUserProfile(): AnalyticsUser['profile'] {
    if (!this.userStatusData?.loggedIn) return undefined;
    return {
      skymiles_number: this.getValidValue(this.userStatusData.smNumber),
      medalion_tier: this.getValidValue(this.userStatusData.membershipLevel),
      encrypted_email: this.encryptEmail(
        this.userStatusData.primaryEmailAddress
      ),
      email_domain: this.getEmailDomain(
        this.userStatusData.primaryEmailAddress
      ),
      company_name: this.getValidValue(this.userStatusData.smeCompanyName),
      available_miles: Number(
        this.getValidValue(this.userStatusData.smBalance)
      ),
      cobrand_type: this.getValidValue(this.userStatusData.smeCardTypes)
    };
  }

  createUserObject(includeProfile = false): AnalyticsUser {
    const user: AnalyticsUser = {
      attributes: this.createUserAttributes()
    };
    if (includeProfile) {
      const profile = this.createUserProfile();
      if (profile) user.profile = profile;
    }
    return user;
  }

  createTextAnalytics(
    elementRef: HTMLElement,
    eventType: string,
    action: string,
    pageSection: string
  ): ClickEventAnalytics {
    const name =
      this.getDataAttribute(elementRef, 'data-acdl-name') ||
      elementRef.textContent?.trim() ||
      '';
    const eventDetail = `${action} click`;
    return {
      ...this.createBaseAnalytics(eventType, eventDetail),
      click: {
        name,
        type: EVENT_CLICK_TYPE.BUTTON,
        page_section: pageSection,
        action: `${action} click`
      }
    };
  }

  createBaseAnalytics(
    eventType: string,
    eventDetail: string,
    includeProfile = false,
    pageAttribute?: string,
    elementRef?: HTMLElement
  ): Omit<ClickEventAnalytics, 'click'> & {
    site_assets?: AnalyticsSiteAssets | null;
  } {
    const baseAnalytics: Omit<ClickEventAnalytics, 'click'> & {
      site_assets?: AnalyticsSiteAssets | null;
    } = {
      event_detail: eventDetail,
      event: eventType,
      user: this.createUserObject(includeProfile)
    };
    if (pageAttribute && elementRef) {
      baseAnalytics.site_assets = this.createSiteAssetsObject(elementRef);
    }
    return baseAnalytics;
  }

  createSiteAssetsObject(elementRef: HTMLElement): AnalyticsSiteAssets | null {
    if (!elementRef) {
      return null;
    }
    const parent = elementRef.closest('[data-acdl-component-type]');
    if (!parent) return null;
    const campaignId = elementRef
      .closest('[data-campaignid]')
      ?.getAttribute('data-campaignid');
    const componentType = parent.getAttribute('data-acdl-component-type');
    if (!componentType) return null;
    return this.buildComponentInfo(parent, componentType, campaignId);
  }

  getElementAction(elementRef: HTMLElement): string {
    return (
      elementRef.getAttribute('href') ||
      elementRef.getAttribute('data-href') ||
      ''
    );
  }
}
